import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Team {
  id: number;
  name: string;
}

interface Squad {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface Match {
  team1Id: number;
  team2Id: number | undefined; // undefined = waiting for opponent, 0 = Bot Blind, >0 = regular team
  team1Name?: string;
  team2Name?: string;
}

interface SquadMatches {
  squadId: number;
  matches: Match[];
  isSaved: boolean;
}

interface ManualMatchingModalProps {
  leagueId: number;
  teams: Team[];
  squads: Squad[];
  existingMatches: Array<{
    squadId: number;
    team1Id: number;
    team2Id: number;
    team1Name: string;
    team2Name: string;
  }>;
  onClose: () => void;
  onMatchesSaved: () => void;
}

const ManualMatchingModal: React.FC<ManualMatchingModalProps> = ({
  leagueId,
  teams,
  squads,
  existingMatches,
  onClose,
  onMatchesSaved
}) => {
  const { t } = useTranslation();
  const [squadMatches, setSquadMatches] = useState<Map<number, SquadMatches>>(new Map());
  const [draggedTeam, setDraggedTeam] = useState<Team | null>(null);
  const [dragOverSquad, setDragOverSquad] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const modalRef = React.useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = React.useRef<number | null>(null);
  
  // Auto-complete state
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [versusCount, setVersusCount] = useState(1);
  const [schedulingType, setSchedulingType] = useState<'standard' | 'finals_by_position' | 'periodic_by_position' | 'always_by_position'>('standard');
  const [positionFrequency, setPositionFrequency] = useState(4);
  const [byeWeeks, setByeWeeks] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [autoCompleting, setAutoCompleting] = useState(false);

  // Add squad state
  const [showAddSquadModal, setShowAddSquadModal] = useState(false);
  const [addingSquad, setAddingSquad] = useState(false);
  const [newSquadName, setNewSquadName] = useState('');
  const [newSquadDate, setNewSquadDate] = useState('');
  const [newSquadStartTime, setNewSquadStartTime] = useState('');
  const [newSquadEndTime, setNewSquadEndTime] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Initialize squad matches from existing data
  useEffect(() => {
    const matchesMap = new Map<number, SquadMatches>();
    
    // Initialize all squads
    squads.forEach(squad => {
      matchesMap.set(squad.id, {
        squadId: squad.id,
        matches: [],
        isSaved: false
      });
    });

    // Populate with existing matches
    existingMatches.forEach(match => {
      const squadMatch = matchesMap.get(match.squadId);
      if (squadMatch) {
        squadMatch.matches.push({
          team1Id: match.team1Id,
          team2Id: match.team2Id,
          team1Name: match.team1Name,
          team2Name: match.team2Name
        });
        squadMatch.isSaved = true; // Existing matches are already saved
      }
    });

    setSquadMatches(matchesMap);
  }, [squads, existingMatches]);

  // Check if auto-complete is available
  useEffect(() => {
    // Check if first N squads are consecutively filled
    let consecutiveFilled = 0;
    for (const squad of squads) {
      const squadMatch = squadMatches.get(squad.id);
      if (squadMatch && squadMatch.isSaved && squadMatch.matches.length > 0) {
        consecutiveFilled++;
      } else {
        break;
      }
    }

    // Show auto-complete if we have consecutive filled squads from start and there are remaining squads
    const shouldShow = consecutiveFilled > 0 && consecutiveFilled < squads.length;
    
    setShowAutoComplete(shouldShow);
  }, [squadMatches, squads]);

  // Auto-scroll functionality for drag and drop
  const handleDragMove = (e: React.DragEvent) => {
    if (!modalRef.current) return;

    const SCROLL_THRESHOLD = 100; // pixels from edge to trigger scroll
    const SCROLL_SPEED = 10; // pixels per interval

    const modalRect = modalRef.current.getBoundingClientRect();
    const mouseY = e.clientY;

    // Clear any existing interval
    if (autoScrollIntervalRef.current) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    // Check if near top edge
    if (mouseY - modalRect.top < SCROLL_THRESHOLD && mouseY > modalRect.top) {
      autoScrollIntervalRef.current = window.setInterval(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = Math.max(0, modalRef.current.scrollTop - SCROLL_SPEED);
        }
      }, 16); // ~60fps
    }
    // Check if near bottom edge
    else if (modalRect.bottom - mouseY < SCROLL_THRESHOLD && mouseY < modalRect.bottom) {
      autoScrollIntervalRef.current = window.setInterval(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = modalRef.current.scrollTop + SCROLL_SPEED;
        }
      }, 16);
    }
  };

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      stopAutoScroll();
    };
  }, []);

  const handleDragStart = (team: Team) => {
    setDraggedTeam(team);
  };

  const handleDragOver = (e: React.DragEvent, squadId: number) => {
    e.preventDefault();
    setDragOverSquad(squadId);
    handleDragMove(e); // Enable auto-scroll during drag
  };

  const handleDragLeave = () => {
    setDragOverSquad(null);
    stopAutoScroll();
  };

  const handleDrop = (e: React.DragEvent, squadId: number) => {
    e.preventDefault();
    setDragOverSquad(null);
    stopAutoScroll(); // Stop auto-scrolling when drop occurs

    if (!draggedTeam) return;

    const squadMatch = squadMatches.get(squadId);
    if (!squadMatch || squadMatch.isSaved) {
      return; // Can't modify saved squads
    }

    // Check if team already in this squad
    const teamAlreadyInSquad = squadMatch.matches.some(
      m => m.team1Id === draggedTeam.id || m.team2Id === draggedTeam.id
    );

    if (teamAlreadyInSquad) {
      setError(`${draggedTeam.name} is already in this week's matches`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Find if there's an incomplete match (only 1 team)
    // Note: team2Id = 0 is Bot Blind (complete match), undefined/null is incomplete
    const incompleteMatchIndex = squadMatch.matches.findIndex(
      m => m.team2Id === undefined || m.team2Id === null
    );

    const newMatches = [...squadMatch.matches];

    if (incompleteMatchIndex !== -1) {
      // Complete the existing match
      newMatches[incompleteMatchIndex] = {
        ...newMatches[incompleteMatchIndex],
        team2Id: draggedTeam.id,
        team2Name: draggedTeam.name
      };
    } else {
      // Start a new match (team2Id undefined = waiting for opponent)
      newMatches.push({
        team1Id: draggedTeam.id,
        team2Id: undefined,
        team1Name: draggedTeam.name,
        team2Name: ''
      });
    }

    setSquadMatches(new Map(squadMatches.set(squadId, {
      ...squadMatch,
      matches: newMatches
    })));

    setDraggedTeam(null);
  };

  const handleRemoveTeamFromMatch = (squadId: number, matchIndex: number, teamPosition: 'team1' | 'team2') => {
    const squadMatch = squadMatches.get(squadId);
    if (!squadMatch || squadMatch.isSaved) return;

    const newMatches = [...squadMatch.matches];
    const match = newMatches[matchIndex];

    if (teamPosition === 'team1') {
      // If removing team1, promote team2 to team1 if it exists
      if (match.team2Id !== undefined && match.team2Id !== null) {
        newMatches[matchIndex] = {
          team1Id: match.team2Id,
          team2Id: undefined,
          team1Name: match.team2Name,
          team2Name: ''
        };
      } else {
        // Remove the match entirely
        newMatches.splice(matchIndex, 1);
      }
    } else {
      // Just clear team2 (set to undefined = waiting for opponent)
      newMatches[matchIndex] = {
        ...match,
        team2Id: undefined,
        team2Name: ''
      };
    }

    setSquadMatches(new Map(squadMatches.set(squadId, {
      ...squadMatch,
      matches: newMatches
    })));
  };

  const isSquadComplete = (squadId: number): boolean => {
    const squadMatch = squadMatches.get(squadId);
    if (!squadMatch) return false;
    
    // Must have at least one match
    if (squadMatch.matches.length === 0) return false;

    // All matches must be complete (both teams assigned, where team2Id can be 0 for Bot Blind)
    const allMatchesComplete = squadMatch.matches.every(
      m => m.team1Id && m.team2Id !== undefined && m.team2Id !== null
    );
    
    // If not all matches complete, squad is not complete
    if (!allMatchesComplete) return false;

    // Check if all teams are matched
    const teamsInMatches = new Set<number>();
    for (const match of squadMatch.matches) {
      teamsInMatches.add(match.team1Id);
      // Don't count Bot Blind (team2Id = 0) as a real team
      if (match.team2Id && match.team2Id !== 0) {
        teamsInMatches.add(match.team2Id);
      }
    }

    // For odd team counts: expect all teams to be matched (one plays Bot Blind)
    // For even team counts: expect all teams to be matched
    const allTeamsMatched = teamsInMatches.size === teams.length;
    
    return allTeamsMatched;
  };

  const getMatchupHistory = (team1Id: number, team2Id: number): number[] => {
    const weeksPlayed: number[] = [];
    
    squadMatches.forEach((squadMatch, squadId) => {
      if (squadMatch.isSaved) {
        squadMatch.matches.forEach(match => {
          const matchHasTeams = 
            (match.team1Id === team1Id && match.team2Id === team2Id) ||
            (match.team1Id === team2Id && match.team2Id === team1Id);
          
          if (matchHasTeams) {
            const squad = squads.find(s => s.id === squadId);
            if (squad) {
              const weekNumber = squads.indexOf(squad) + 1;
              weeksPlayed.push(weekNumber);
            }
          }
        });
      }
    });

    return weeksPlayed;
  };

  const handleSaveSquad = async (squadId: number) => {
    const squadMatch = squadMatches.get(squadId);
    if (!squadMatch || !isSquadComplete(squadId)) return;

    setSaving(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/manual-matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          squadId,
          matches: squadMatch.matches.map(m => ({
            team1Id: m.team1Id,
            team2Id: m.team2Id
          }))
        })
      });

      if (response.ok) {
        // Mark as saved
        setSquadMatches(new Map(squadMatches.set(squadId, {
          ...squadMatch,
          isSaved: true
        })));

        // Notify parent to refresh data
        onMatchesSaved();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save matches');
      }
    } catch (error) {
      console.error('Save squad matches error:', error);
      setError('Failed to save matches');
    } finally {
      setSaving(false);
    }
  };

  const handleValidateRemaining = async () => {
    setValidating(true);
    setValidationResult(null);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/validate-remaining`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          versusCount,
          schedulingType,
          positionFrequency: schedulingType === 'periodic_by_position' ? positionFrequency : undefined,
          byeWeeks
        })
      });

      const result = await response.json();
      setValidationResult(result);

      if (!result.isValid) {
        setError(result.error);
      }
    } catch (error) {
      console.error('Validate remaining error:', error);
      setError('Failed to validate remaining weeks');
    } finally {
      setValidating(false);
    }
  };

  const handleAutoCompleteRemaining = async () => {
    if (!validationResult?.isValid) return;

    setAutoCompleting(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/auto-complete-remaining`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          versusCount,
          schedulingType,
          positionFrequency: schedulingType === 'periodic_by_position' ? positionFrequency : undefined,
          byeWeeks
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Success! ${result.message}`);
        onMatchesSaved();
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to auto-complete remaining weeks');
      }
    } catch (error) {
      console.error('Auto-complete error:', error);
      setError('Failed to auto-complete remaining weeks');
    } finally {
      setAutoCompleting(false);
    }
  };

  const handleOpenAddSquadModal = () => {
    // Calculate auto-fill values based on the last squad
    if (squads.length > 0) {
      const lastSquad = squads[squads.length - 1];
      
      // Extract week number from name and increment
      const weekMatch = lastSquad.name.match(/Week (\d+)/i);
      const weekNumber = weekMatch ? parseInt(weekMatch[1]) + 1 : squads.length + 1;
      
      // Extract description pattern and increment week number
      const descMatch = lastSquad.name.match(/^(.+?)\s*-?\s*Week \d+/i);
      const baseDesc = descMatch ? descMatch[1].trim() : 'Wednesday night squad';
      
      setNewSquadName(`Week ${weekNumber}`);
      
      // Add 7 days to the last date
      const lastDate = new Date(lastSquad.date);
      lastDate.setDate(lastDate.getDate() + 7);
      const newDateStr = lastDate.toISOString().split('T')[0];
      setNewSquadDate(newDateStr);
      
      setNewSquadStartTime(lastSquad.startTime || '18:00');
      setNewSquadEndTime(lastSquad.endTime || '21:00');
    } else {
      // Default values if no squads exist
      setNewSquadName('Week 1');
      setNewSquadDate('');
      setNewSquadStartTime('18:00');
      setNewSquadEndTime('21:00');
    }
    
    setShowAddSquadModal(true);
  };

  const handleAddSquad = async () => {
    if (!newSquadName || !newSquadDate || !newSquadStartTime) {
      setError('Please fill in name, date, and start time');
      return;
    }

    setAddingSquad(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/schedule/${leagueId}/add-squad`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: newSquadName,
          date: newSquadDate,
          startTime: newSquadStartTime,
          endTime: newSquadEndTime
        })
      });

      if (response.ok) {
        // Show success message with animation
        setShowSuccessMessage(true);
        
        // Auto-close modals after 3 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
          setShowAddSquadModal(false);
          onMatchesSaved(); // Refresh parent to reload squads
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add squad');
      }
    } catch (error) {
      console.error('Add squad error:', error);
      setError('Failed to add squad');
    } finally {
      setAddingSquad(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div ref={modalRef} className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-t-xl sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{t('manualMatching.title')}</h2>
              <p className="text-orange-100 text-sm mt-1">{t('manualMatching.subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-orange-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Warning Section */}
        <div className="p-6 bg-yellow-50 border-b border-yellow-200">
          <div className="flex items-start space-x-3">
            <div className="text-yellow-600 text-2xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-yellow-900 mb-2">{t('manualMatching.warning.title')}</h3>
              <p className="text-yellow-800 mb-3">
                {t('manualMatching.warning.description')}
              </p>
              <div className="bg-white rounded-lg p-4 space-y-2">
                <p className="text-sm text-gray-700 font-medium">{t('manualMatching.warning.pointers.title')}</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 ml-2">
                  <li>{t('manualMatching.warning.pointers.item1')}</li>
                  <li>{t('manualMatching.warning.pointers.item2')}</li>
                  <li>{t('manualMatching.warning.pointers.item3')}</li>
                  <li>{t('manualMatching.warning.pointers.item4')}</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 bg-red-100 border border-red-400 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-red-600">⚠️</span>
              <p className="text-red-800 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-600 hover:text-red-800"
            >
              ✕
            </button>
          </div>
        )}

        {/* Available Teams Pool */}
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('manualMatching.availableTeams')}</h3>
          <div className="flex flex-wrap gap-2">
            {/* Bot Blind Special Team (for odd team counts) */}
            <div
              draggable
              onDragStart={() => handleDragStart({id: 0, name: 'Blind'})}
              onDragEnd={stopAutoScroll}
              className="px-4 py-2 bg-purple-100 border-2 border-purple-400 rounded-lg text-purple-800 font-bold cursor-move hover:bg-purple-200 transition-colors"
              title={t('manualMatching.botBlindTooltip') || 'Blind - Use for odd team counts'}
            >
              {t('manualMatching.blind') || 'Blind'}
            </div>
            
            {/* Regular Teams */}
            {teams.map(team => (
              <div
                key={team.id}
                draggable
                onDragStart={() => handleDragStart(team)}
                onDragEnd={stopAutoScroll}
                className="px-4 py-2 bg-blue-100 border border-blue-300 rounded-lg text-blue-800 font-medium cursor-move hover:bg-blue-200 transition-colors"
              >
                {team.name}
              </div>
            ))}
          </div>
        </div>

        {/* Squads/Weeks Grid */}
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">{t('manualMatching.weeksSchedule')}</h3>
            <button
              onClick={handleOpenAddSquadModal}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center space-x-2"
            >
              <span>➕</span>
              <span>{t('manualMatching.addSquad')}</span>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {squads.map((squad, index) => {
              const squadMatch = squadMatches.get(squad.id);
              const isComplete = isSquadComplete(squad.id);
              const isSaved = squadMatch?.isSaved || false;

              return (
                <div
                  key={squad.id}
                  onDragOver={(e) => handleDragOver(e, squad.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, squad.id)}
                  className={`
                    relative border-2 rounded-lg p-4 transition-all
                    ${isSaved ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-300'}
                    ${dragOverSquad === squad.id && !isSaved ? 'border-blue-500 bg-blue-50 scale-105' : ''}
                    ${isComplete && !isSaved ? 'border-yellow-500 bg-yellow-50' : ''}
                  `}
                >
                  {/* Week Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-gray-800">{t('manualMatching.week')} {index + 1}</h4>
                      <p className="text-xs text-gray-600">{squad.name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(squad.date).toLocaleDateString()}
                      </p>
                    </div>
                    {isComplete && !isSaved && (
                      <div className="text-green-600 text-2xl">✓</div>
                    )}
                    {isSaved && (
                      <div className="text-green-700 text-2xl">🔒</div>
                    )}
                  </div>

                  {/* Matches */}
                  <div className="space-y-2 min-h-[100px] max-h-[400px] overflow-y-auto">
                    {squadMatch?.matches.map((match, matchIndex) => {
                      const history = match.team2Id && match.team2Id !== 0 
                        ? getMatchupHistory(match.team1Id, match.team2Id)
                        : [];
                      
                      return (
                        <div key={matchIndex} className="bg-white rounded p-2 border border-gray-200">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex-1">
                              <div className="flex items-center space-x-1">
                                <span className="text-sm font-medium text-gray-800">
                                  {match.team1Name || `Team ${match.team1Id}`}
                                </span>
                                {!isSaved && (
                                  <button
                                    onClick={() => handleRemoveTeamFromMatch(squad.id, matchIndex, 'team1')}
                                    className="text-red-500 hover:text-red-700 text-xs"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">vs</div>
                              {match.team2Id === 0 ? (
                                <div className="flex items-center space-x-1">
                                  <span className="text-sm font-bold text-purple-800">
                                    {t('manualMatching.blind') || 'Blind'}
                                  </span>
                                  {!isSaved && (
                                    <button
                                      onClick={() => handleRemoveTeamFromMatch(squad.id, matchIndex, 'team2')}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              ) : match.team2Id ? (
                                <div className="flex items-center space-x-1">
                                  <span className="text-sm font-medium text-gray-800">
                                    {match.team2Name || `Team ${match.team2Id}`}
                                  </span>
                                  {!isSaved && (
                                    <button
                                      onClick={() => handleRemoveTeamFromMatch(squad.id, matchIndex, 'team2')}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs italic text-gray-400">{t('manualMatching.waitingForOpponent')}</span>
                              )}
                            </div>
                          </div>
                          {history.length > 0 && (
                            <p className="text-xs text-orange-600 mt-1">
                              ⚠️ {t('manualMatching.alreadyPlayed', { weeks: history.join(', ') })}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {(!squadMatch?.matches || squadMatch.matches.length === 0) && (
                      <p className="text-gray-400 text-sm italic text-center py-8">
                        {t('manualMatching.dragTeamsHere')}
                      </p>
                    )}
                  </div>

                  {/* Save Button */}
                  {!isSaved && isComplete && (
                    <button
                      onClick={() => handleSaveSquad(squad.id)}
                      disabled={saving}
                      className="mt-3 w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {saving ? t('manualMatching.saving') : t('manualMatching.saveWeek')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Auto-Complete Section */}
        {showAutoComplete && (
          <div className="p-6 border-t border-gray-200 bg-blue-50">
            <h3 className="text-lg font-bold text-gray-800 mb-3">{t('manualMatching.autoComplete.title')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('manualMatching.autoComplete.description')}
            </p>

            {/* Info Box */}
            <div className="mb-4 p-3 bg-blue-100 border border-blue-300 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ {t('manualMatching.autoComplete.howItWorks.title')}</strong> {t('manualMatching.autoComplete.howItWorks.description')}
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 space-y-4">
              {/* Versus Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manualMatching.autoComplete.versusCount')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={versusCount}
                  onChange={(e) => setVersusCount(parseInt(e.target.value) || 1)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Scheduling Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manualMatching.autoComplete.schedulingType.label')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="standard"
                      checked={schedulingType === 'standard'}
                      onChange={(e) => setSchedulingType('standard')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{t('manualMatching.autoComplete.schedulingType.standard')}</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="finals_by_position"
                      checked={schedulingType === 'finals_by_position'}
                      onChange={(e) => setSchedulingType('finals_by_position')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{t('manualMatching.autoComplete.schedulingType.finalsByPosition')}</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="periodic_by_position"
                      checked={schedulingType === 'periodic_by_position'}
                      onChange={(e) => setSchedulingType('periodic_by_position')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{t('manualMatching.autoComplete.schedulingType.periodicByPosition')}</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="schedulingType"
                      value="always_by_position"
                      checked={schedulingType === 'always_by_position'}
                      onChange={(e) => setSchedulingType('always_by_position')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{t('manualMatching.autoComplete.schedulingType.alwaysByPosition')}</span>
                  </label>
                </div>
              </div>

              {/* Position Frequency (conditional) */}
              {schedulingType === 'periodic_by_position' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('manualMatching.autoComplete.positionFrequency')}
                  </label>
                  <input
                    type="number"
                    min="2"
                    max="20"
                    value={positionFrequency}
                    onChange={(e) => setPositionFrequency(parseInt(e.target.value) || 4)}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Bye Weeks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manualMatching.autoComplete.byeWeeks.label')}
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  {t('manualMatching.autoComplete.byeWeeks.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {squads.map((squad, index) => {
                    const squadDateStr = new Date(squad.date).toISOString().split('T')[0];
                    const isSelected = byeWeeks.includes(squadDateStr);
                    return (
                      <button
                        key={squad.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setByeWeeks(byeWeeks.filter(d => d !== squadDateStr));
                          } else {
                            setByeWeeks([...byeWeeks, squadDateStr]);
                          }
                        }}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          isSelected 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Week {index + 1}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleValidateRemaining}
                  disabled={validating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {validating ? t('manualMatching.autoComplete.validating') : t('manualMatching.autoComplete.checkButton')}
                </button>

                {validationResult?.isValid && (
                  <button
                    onClick={handleAutoCompleteRemaining}
                    disabled={autoCompleting}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {autoCompleting ? t('manualMatching.autoComplete.completing') : t('manualMatching.autoComplete.executeButton')}
                  </button>
                )}
              </div>

              {validationResult && (
                <div className={`p-3 rounded-lg ${validationResult.isValid ? 'bg-green-100 border border-green-400' : 'bg-red-100 border border-red-400'}`}>
                  <p className={`text-sm ${validationResult.isValid ? 'text-green-800' : 'text-red-800'}`}>
                    {validationResult.isValid ? '✓ ' : '✗ '}
                    {validationResult.message || validationResult.error}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>

      {/* Add Squad Modal */}
      {showAddSquadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-xl">
              <h3 className="text-xl font-bold">{t('manualMatching.addSquadModal.title')}</h3>
            </div>
            
            {showSuccessMessage ? (
              <div className="p-6 text-center animate-fade-in">
                <div className="text-6xl mb-4 animate-bounce">✅</div>
                <h4 className="text-xl font-bold text-green-600 mb-3">{t('manualMatching.addSquadModal.success.title')}</h4>
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ {t('manualMatching.addSquadModal.success.remember')}</strong> {t('manualMatching.addSquadModal.success.reminder')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('manualMatching.addSquadModal.squadName')}
                  </label>
                  <input
                    type="text"
                    value={newSquadName}
                    onChange={(e) => setNewSquadName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={t('manualMatching.addSquadModal.squadNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('manualMatching.addSquadModal.date')}
                  </label>
                  <input
                    type="date"
                    value={newSquadDate}
                    onChange={(e) => setNewSquadDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('manualMatching.addSquadModal.startTime')}
                    </label>
                    <input
                      type="time"
                      value={newSquadStartTime}
                      onChange={(e) => setNewSquadStartTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('manualMatching.addSquadModal.endTime')}
                    </label>
                    <input
                      type="time"
                      value={newSquadEndTime}
                      onChange={(e) => setNewSquadEndTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={handleAddSquad}
                    disabled={addingSquad}
                    className="flex-1 bg-purple-600 text-white py-2 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {addingSquad ? t('manualMatching.addSquadModal.adding') : t('manualMatching.addSquadModal.addButton')}
                  </button>
                  <button
                    onClick={() => setShowAddSquadModal(false)}
                    disabled={addingSquad}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-400 disabled:opacity-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualMatchingModal;
